import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import RangeCalendar from '../components/calendar/RangeCalendar';
import TimePicker from '../components/calendar/TimePicker';
import { useBookingStore } from '../stores/bookingStore';
import { validateDateRange } from '../utils/validation';
import { SEOContent } from '../components/SEOContent';
import { setSEO } from '../utils/seo';
import Header from '../components/Header';
import Footer from '../components/Footer';
import { apiClient } from '../api/client';

interface Review {
  id: number;
  vehicle_name: string;
  rating: number;
  content: string;
  author_name: string;
  created_at: string;
}

const HomePage: React.FC = () => {
  const navigate = useNavigate();
  const { t, i18n } = useTranslation();
  const { startDateTime, endDateTime, selectedCarClasses, selectedFuels, setStartDateTime, setEndDateTime, setSelectedCarClasses, setSelectedFuels } = useBookingStore();

  // SEO 설정
  useEffect(() => {
    window.scrollTo(0, 0);
    setSEO('home');
  }, []);

  const [selectedStartDate, setSelectedStartDate] = useState<Date | null>(
    startDateTime ? new Date(startDateTime) : null
  );
  const [selectedEndDate, setSelectedEndDate] = useState<Date | null>(
    endDateTime ? new Date(endDateTime) : null
  );

  // 현재 시간 이후로 초기값 설정 (30분 단위, 8~22시 제한)
  const now = new Date();
  const currentHour = now.getHours();
  const currentMinute = now.getMinutes();

  // 다음 30분 단위 시간으로 반올림 (8~22시 제한)
  const getNextHalfHour = (hour: number, minute: number): string => {
    let nextHour = hour;
    let nextMinute = 0;

    if (minute < 30) {
      nextMinute = 30;
    } else {
      nextHour = hour + 1;
      nextMinute = 0;
    }

    // 8~22시 범위로 제한
    if (nextHour < 8) {
      nextHour = 8;
      nextMinute = 0;
    } else if (nextHour > 22) {
      nextHour = 8;
      nextMinute = 0;
    } else if (nextHour === 22 && nextMinute > 0) {
      nextHour = 22;
      nextMinute = 0;
    }

    return `${nextHour.toString().padStart(2, '0')}:${nextMinute.toString().padStart(2, '0')}`;
  };

  const initialStartTime = getNextHalfHour(currentHour, currentMinute);
  const [startTime, setStartTime] = useState<string>(initialStartTime);
  const [endTime, setEndTime] = useState<string>('18:00');
  const [error, setError] = useState<string>('');
  const [carClasses, setCarClasses] = useState<string[]>([]);
  const [reviews, setReviews] = useState<Review[]>([]);
  const [currentReviewIndex, setCurrentReviewIndex] = useState(0);
  const [isSliding, setIsSliding] = useState(false);
  const [slideDirection, setSlideDirection] = useState<'left' | 'right' | null>(null);
  const carClassLabels: Record<string, string> = {
    경차: 'Compact',
    소형: 'Economy',
    준중형: 'Compact Sedan',
    중형: 'Midsize',
    대형: 'Full-size',
    수입: 'Imported',
    RV: 'RV',
    SUV: 'SUV',
  };
  const getCarClassLabel = (carClass: string) => (
    i18n.language.startsWith('en') ? (carClassLabels[carClass] || carClass) : carClass
  );

  // Fetch car classes
  useEffect(() => {
    const fetchCarClasses = async () => {
      try {
        const response = await apiClient.get('/vehicles/classes');
        if (response.data.code === '0' && Array.isArray(response.data.data)) {
          const classes = response.data.data.map((item: any) =>
            item.carClassName || item.carClassname || item.name
          );
          setCarClasses(classes);
        }
      } catch (err) {
        console.error('Failed to fetch car classes:', err);
      }
    };

    fetchCarClasses();
  }, []);

  // Fetch 5-star reviews
  useEffect(() => {
    const fetchReviews = async () => {
      try {
        const response = await apiClient.get('/reviews?rating=5.0&limit=20');
        setReviews(response.data);
      } catch (err) {
        console.error('Failed to fetch reviews:', err);
      }
    };

    fetchReviews();
  }, []);

  // 시작 날짜가 오늘인지 확인
  const isStartDateToday = selectedStartDate &&
    selectedStartDate.toDateString() === now.toDateString();

  // 종료 날짜가 시작 날짜와 같은 날인지 확인
  const isSameDay = selectedStartDate && selectedEndDate &&
    selectedStartDate.toDateString() === selectedEndDate.toDateString();

  // 최소 종료 시간 계산 (시작 시간 + 5시간)
  const getMinEndTime = (): string => {
    if (!isSameDay) return '00:00';

    const [startHour, startMinute] = startTime.split(':').map(Number);
    const startTotalMinutes = startHour * 60 + startMinute;
    const minEndTotalMinutes = startTotalMinutes + 5 * 60; // 5시간 추가

    const minEndHour = Math.floor(minEndTotalMinutes / 60);
    const minEndMinute = minEndTotalMinutes % 60;

    // 24시간 넘어가면 다음날로 (00:00으로 반환하여 제한 없음)
    if (minEndHour >= 24) return '00:00';

    return `${minEndHour.toString().padStart(2, '0')}:${minEndMinute.toString().padStart(2, '0')}`;
  };

  const handleRangeSelect = (start: Date | null, end: Date | null) => {
    setSelectedStartDate(start);
    setSelectedEndDate(end);
    setError('');
  };

  const handleSearch = () => {
    if (!selectedStartDate || !selectedEndDate) {
      setError(t('home.selectBothDates'));
      return;
    }

    // Parse time strings (HH:mm format)
    const [startHour, startMinute] = startTime.split(':').map(Number);
    const [endHour, endMinute] = endTime.split(':').map(Number);

    // Create datetime objects using year, month, day to avoid timezone issues
    const start = new Date(
      selectedStartDate.getFullYear(),
      selectedStartDate.getMonth(),
      selectedStartDate.getDate(),
      startHour,
      startMinute,
      0,
      0
    );

    const end = new Date(
      selectedEndDate.getFullYear(),
      selectedEndDate.getMonth(),
      selectedEndDate.getDate(),
      endHour,
      endMinute,
      0,
      0
    );

    // Validate date range
    const validation = validateDateRange(start, end);
    if (!validation.valid) {
      setError(validation.error || t('home.invalidDate'));
      return;
    }

    // Save to store
    console.log('HomePage - Setting DateTime:', {
      startTime,
      endTime,
      start: start.toString(),
      startHours: start.getHours(),
      startMinutes: start.getMinutes(),
      end: end.toString(),
      endHours: end.getHours(),
      endMinutes: end.getMinutes(),
    });
    setStartDateTime(start);
    setEndDateTime(end);

    // Navigate to vehicle list
    navigate('/vehicles');
  };

  const handlePrevReview = () => {
    if (isSliding) return;
    setSlideDirection('left'); // 왼쪽 버튼: 왼쪽에서 오른쪽으로 이동
    setIsSliding(true);
    setTimeout(() => {
      setCurrentReviewIndex((prev) => {
        const newIndex = prev - 3;
        return newIndex < 0 ? reviews.length + newIndex : newIndex;
      });
      setIsSliding(false);
      setSlideDirection(null);
    }, 300);
  };

  const handleNextReview = () => {
    if (isSliding) return;
    setSlideDirection('right'); // 오른쪽 버튼: 오른쪽에서 왼쪽으로 이동
    setIsSliding(true);
    setTimeout(() => {
      setCurrentReviewIndex((prev) => (prev + 3) % reviews.length);
      setIsSliding(false);
      setSlideDirection(null);
    }, 300);
  };

  const formatDate = (dateString: string) => {
    const date = new Date(dateString);
    return date.toLocaleDateString(i18n.language === 'en' ? 'en-US' : 'ko-KR', { year: 'numeric', month: 'long', day: 'numeric' });
  };

  const maskName = (name: string) => {
    const suffix = t('home.customerSuffix');
    if (name.length <= 2) {
      return `${name[0]}● ${suffix}`;
    }
    const first = name[0];
    const last = name[name.length - 1];
    const middle = '●'.repeat(name.length - 2);
    return `${first}${middle}${last} ${suffix}`;
  };

  return (
    <div className="min-h-screen bg-gradient-to-b from-white to-gray-50">
      {/* Header */}
      <Header />

      {/* Main Content */}
      <div className="container mx-auto px-4 py-8">
        <div className="max-w-5xl mx-auto">
          {/* Header Text */}
          <div className="text-center mb-6">
            <h2 className="text-2xl font-bold text-gray-900">
              {String(t('home.heroTitle')).split('\n').map((line: string, index: number) => (
                <React.Fragment key={line}>
                  {index > 0 && <br />}
                  {line}
                </React.Fragment>
              ))}
            </h2>
          </div>

          {/* Instructions */}
          <div className="mb-6">
            <div className="bg-white rounded-2xl border border-gray-200 p-5 shadow-sm">
              <div className="flex items-start gap-3">
                <div className="flex-shrink-0 w-8 h-8 rounded-full bg-gray-100 flex items-center justify-center">
                  <svg className="w-4 h-4 text-gray-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                  </svg>
                </div>
                <div className="flex-1">
                  <h3 className="text-base font-semibold text-gray-900 mb-2">{t('home.howToBook')}</h3>
                  <ol className="space-y-1.5 text-sm text-gray-600">
                    <li className="flex items-start">
                      <span className="font-medium mr-2">1.</span>
                      <span>{t('home.stepSelectPeriod')}</span>
                    </li>
                    <li className="flex items-start">
                      <span className="font-medium mr-2">2.</span>
                      <span>{t('home.stepSelectTime')}</span>
                    </li>
                    <li className="flex items-start">
                      <span className="font-medium mr-2">3.</span>
                      <span>{t('home.stepCheckVehicles')}</span>
                    </li>
                  </ol>
                </div>
              </div>
            </div>
          </div>

          {/* Calendar and Time Selection */}
          <div className="bg-white rounded-2xl border border-gray-200 p-6 shadow-sm mb-6">
            <h2 className="text-xl font-semibold text-gray-900 mb-4">{t('home.periodAndTime')}</h2>

            <div className="grid lg:grid-cols-2 gap-6">
              {/* Left: Calendar */}
              <div>
                <h3 className="text-base font-medium text-gray-700 mb-3">{t('home.rentalPeriod')}</h3>
                <RangeCalendar
                  startDate={selectedStartDate}
                  endDate={selectedEndDate}
                  onSelectRange={handleRangeSelect}
                />
              </div>

              {/* Right: Time Selection and Car Class */}
              <div className="space-y-6">
                {/* Time Selection */}
                <div className="grid grid-cols-2 gap-4">
                  {/* Start Time */}
                  <div>
                    <TimePicker
                      selectedTime={startTime}
                      onSelectTime={setStartTime}
                      label={t('home.rentalTime')}
                      minTime={isStartDateToday ? initialStartTime : undefined}
                    />
                  </div>

                  {/* End Time */}
                  <div>
                    <TimePicker
                      selectedTime={endTime}
                      onSelectTime={setEndTime}
                      label={t('home.returnTime')}
                      minTime={getMinEndTime()}
                    />
                  </div>
                </div>

                {/* Car Class Selection */}
                <div>
                  <h3 className="text-base font-medium text-gray-700 mb-3">{t('home.carClass')}</h3>
                  <div className="flex flex-wrap gap-2">
                    <button
                      onClick={() => setSelectedCarClasses([])}
                      className={`px-4 py-2 rounded-lg text-sm font-medium transition-all border-2 ${
                        selectedCarClasses.length === 0
                          ? 'border-blue-600 text-blue-600 bg-white'
                          : 'border-gray-300 text-gray-700 bg-white hover:border-gray-400'
                      }`}
                    >
                      {t('home.all')}
                    </button>
                    {carClasses.map((carClass) => {
                      const isSelected = selectedCarClasses.includes(carClass);
                      return (
                        <button
                          key={carClass}
                          onClick={() => {
                            if (isSelected) {
                              setSelectedCarClasses(selectedCarClasses.filter(c => c !== carClass));
                            } else {
                              setSelectedCarClasses([...selectedCarClasses, carClass]);
                            }
                          }}
                          className={`px-4 py-2 rounded-lg text-sm font-medium transition-all border-2 ${
                            isSelected
                              ? 'border-blue-600 text-blue-600 bg-white'
                              : 'border-gray-300 text-gray-700 bg-white hover:border-gray-400'
                          }`}
                        >
                          {getCarClassLabel(carClass)}
                        </button>
                      );
                    })}
                  </div>
                </div>

                {/* Fuel Type Selection */}
                <div>
                  <h3 className="text-base font-medium text-gray-700 mb-3">{t('home.fuelType')}</h3>
                  <div className="flex flex-wrap gap-2">
                    <button
                      onClick={() => setSelectedFuels([])}
                      className={`px-4 py-2 rounded-lg text-sm font-medium transition-all border-2 ${
                        selectedFuels.length === 0
                          ? 'border-blue-600 text-blue-600 bg-white'
                          : 'border-gray-300 text-gray-700 bg-white hover:border-gray-400'
                      }`}
                    >
                      {t('home.all')}
                    </button>
                    {[
                      { value: '휘발유', label: t('home.gasoline') },
                      { value: 'LPG', label: t('home.lpg') },
                      { value: '경유', label: t('home.diesel') },
                      { value: '전기', label: t('home.electric') },
                      { value: '하이브리드', label: t('home.hybrid') },
                    ].map((fuel) => {
                      const fuelValue = fuel.value;
                      const isSelected = selectedFuels.includes(fuelValue);
                      return (
                        <button
                          key={fuelValue}
                          onClick={() => {
                            if (isSelected) {
                              setSelectedFuels(selectedFuels.filter(f => f !== fuelValue));
                            } else {
                              setSelectedFuels([...selectedFuels, fuelValue]);
                            }
                          }}
                          className={`px-4 py-2 rounded-lg text-sm font-medium transition-all border-2 ${
                            isSelected
                              ? 'border-blue-600 text-blue-600 bg-white'
                              : 'border-gray-300 text-gray-700 bg-white hover:border-gray-400'
                          }`}
                        >
                          {fuel.label}
                        </button>
                      );
                    })}
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Error Message */}
          {error && (
            <div className="bg-red-50 border border-red-200 rounded-2xl p-4 mb-6">
              <div className="flex items-center gap-3">
                <svg className="w-5 h-5 text-red-600 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                </svg>
                <span className="text-red-900 font-medium">{error}</span>
              </div>
            </div>
          )}

          {/* Info Text */}
          <p className="text-center text-sm text-gray-600 mb-4">
            {t('home.priceHint')}
          </p>

          {/* Search Button */}
          <button
            onClick={handleSearch}
            className="w-full bg-gray-900 hover:bg-gray-800 text-white text-lg font-medium py-4 px-8 rounded-xl transition-all duration-200 shadow-sm hover:shadow-md"
          >
            {t('home.checkPrice')}
          </button>
        </div>
      </div>

      {/* Features */}
      <div className="py-24 bg-gray-50">
        <div className="container mx-auto px-4">
          <div className="max-w-5xl mx-auto">
            <h2 className="text-3xl font-semibold text-gray-900 text-center mb-16">{t('home.whyTitle')}</h2>
            <div className="grid md:grid-cols-3 gap-8">
              <div className="bg-white rounded-2xl border border-gray-200 p-8 shadow-sm hover:shadow-md transition-shadow">
                <div className="w-12 h-12 rounded-xl bg-gray-100 flex items-center justify-center mb-5">
                  <svg className="w-6 h-6 text-gray-700" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
                  </svg>
                </div>
                <h3 className="text-xl font-semibold text-gray-900 mb-3">{t('home.fastBookingTitle')}</h3>
                <p className="text-gray-600 leading-relaxed">{t('home.fastBookingDesc')}</p>
              </div>

              <div className="bg-white rounded-2xl border border-gray-200 p-8 shadow-sm hover:shadow-md transition-shadow">
                <div className="w-12 h-12 rounded-xl bg-gray-100 flex items-center justify-center mb-5">
                  <svg className="w-6 h-6 text-gray-700" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z" />
                  </svg>
                </div>
                <h3 className="text-xl font-semibold text-gray-900 mb-3">{t('home.safePaymentTitle')}</h3>
                <p className="text-gray-600 leading-relaxed">{t('home.safePaymentDesc')}</p>
              </div>

              <div className="bg-white rounded-2xl border border-gray-200 p-8 shadow-sm hover:shadow-md transition-shadow">
                <div className="w-12 h-12 rounded-xl bg-gray-100 flex items-center justify-center mb-5">
                  <svg className="w-6 h-6 text-gray-700" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                  </svg>
                </div>
                <h3 className="text-xl font-semibold text-gray-900 mb-3">{t('home.cleanCarTitle')}</h3>
                <p className="text-gray-600 leading-relaxed">{t('home.cleanCarDesc')}</p>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Customer Reviews Section */}
      {reviews.length > 0 && (
        <div className="py-16 border-t border-gray-200">
          <div className="container mx-auto px-4">
            <div className="max-w-6xl mx-auto">
              <div className="text-center mb-12">
                <h2 className="text-3xl font-bold text-gray-900 mb-3">{t('home.reviewsTitle')}</h2>
                <p className="text-gray-600">
                  {String(t('home.reviewsSubtitle')).split('\n').map((line: string, index: number) => (
                    <React.Fragment key={line}>
                      {index > 0 && <br />}
                      {line}
                    </React.Fragment>
                  ))}
                </p>
              </div>

              <div className="relative flex items-center gap-4">
                {/* Left Navigation Button */}
                <button
                  onClick={handlePrevReview}
                  className="flex-shrink-0 p-3 rounded-full bg-white border-2 border-gray-300 hover:border-gray-500 hover:bg-gray-100 transition-all shadow-sm"
                  aria-label={t('home.prevReview')}
                >
                  <svg className="w-6 h-6 text-gray-700" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
                  </svg>
                </button>

                {/* Review Cards - 3 in a row with overflow hidden for sliding effect */}
                <div className="flex-1 overflow-hidden">
                  <div
                    className={`grid md:grid-cols-3 gap-4 transition-transform duration-300 ease-in-out ${
                      isSliding
                        ? slideDirection === 'right'
                          ? 'transform -translate-x-12'  // 오른쪽 버튼: 왼쪽으로 이동
                          : 'transform translate-x-12'   // 왼쪽 버튼: 오른쪽으로 이동
                        : 'transform translate-x-0'
                    }`}
                  >
                    {[0, 1, 2].map((offset) => {
                      const reviewIndex = (currentReviewIndex + offset) % reviews.length;
                      const review = reviews[reviewIndex];
                      return (
                        <div
                          key={`${reviewIndex}-${currentReviewIndex}`}
                          className="bg-white rounded-2xl border-2 border-gray-200 p-5 shadow-md h-48 flex flex-col"
                        >
                          {/* Stars */}
                          <div className="flex justify-center mb-2">
                            {[...Array(5)].map((_, i) => (
                              <svg
                                key={i}
                                className="w-4 h-4 text-yellow-400 fill-current"
                                viewBox="0 0 20 20"
                              >
                                <path d="M10 15l-5.878 3.09 1.123-6.545L.489 6.91l6.572-.955L10 0l2.939 5.955 6.572.955-4.756 4.635 1.123 6.545z" />
                              </svg>
                            ))}
                          </div>

                          {/* Review Content */}
                          <div className="text-center flex-1 flex flex-col justify-between">
                            <p className="text-gray-700 text-sm leading-relaxed mb-3 line-clamp-3">
                              "{review.content}"
                            </p>
                            <div className="space-y-0.5">
                              <p className="text-gray-900 font-semibold text-sm">
                                {maskName(review.author_name)}
                              </p>
                              <p className="text-gray-500 text-xs">
                                {review.vehicle_name}
                              </p>
                              <p className="text-gray-400 text-xs">
                                {formatDate(review.created_at)}
                              </p>
                            </div>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                </div>

                {/* Right Navigation Button */}
                <button
                  onClick={handleNextReview}
                  className="flex-shrink-0 p-3 rounded-full bg-white border-2 border-gray-300 hover:border-gray-500 hover:bg-gray-100 transition-all shadow-sm"
                  aria-label={t('home.nextReview')}
                >
                  <svg className="w-6 h-6 text-gray-700" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                  </svg>
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* SEO Content Section */}
      <SEOContent />

      {/* Footer */}
      <Footer />
    </div>
  );
};

export default HomePage;
